import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Globe, Workflow, Plus, Zap, Clock, Bookmark, Store, MessageSquare } from 'lucide-react';
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuTrigger,
} from '@dashai/components/ui/navigation-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@dashai/components/ui/collapsible';
import { cn } from '@dashai/lib/utils';
import { navPillClass } from './navStyles';
import { DASHAI_ROUTES, DASHBOARD_ICONS, browseEntryHref, chatRoute, launchInPage } from '@dashai/lib/browseNavigation';
import { byLatestMessage, chatDisplayTitle } from '@dashai/lib/toChatSession';
import type { ChatSession } from '@dashai/types/dashai';
import type { LaunchWorkflow } from '@dashai/lib/browseNavigation';
import { groupSfaVariants } from '@dashai/utils/sfaVariants';
import type { BrowseEntry } from '@dashai/utils/sfaVariants';
import type { PersonaVisibility } from '@dashai/hooks/usePersonaVisibility';
import { RECENT_CHAT_COUNT } from '@dashai/hooks/useRecentChats';
import type { DashboardCard } from '@/data/dashboardCards';
import type { WorkflowsLabel } from '@dashai/utils/queryScope';

const ROW_CLASS =
  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-popover-foreground outline-none hover:bg-accent focus-visible:bg-accent';

// A row's height and padding with no content, so a list that is still loading
// holds the space its rows will take.
function PlaceholderRow() {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5" aria-hidden="true" data-testid="panel-placeholder-row">
      <span className="h-4 w-4 shrink-0 animate-pulse rounded bg-muted" />
      <span className="flex h-5 flex-1 items-center">
        <span className="h-3 w-full animate-pulse rounded bg-muted" />
      </span>
    </div>
  );
}

function MenuNote({ children }: { children: React.ReactNode }) {
  return <p className="px-2 py-1.5 text-sm text-muted-foreground">{children}</p>;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="px-2 pb-1 pt-1.5 text-xs font-semibold text-muted-foreground">{children}</p>;
}

export function PanelSeparator() {
  return <div role="separator" className="-mx-1.5 my-1 h-px bg-border" />;
}

function MenuChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-auto shrink-0 rounded-full bg-muted px-1.5 text-[10px] font-semibold uppercase text-muted-foreground">
      {children}
    </span>
  );
}

// One destination in a panel: a router link, so a modified click opens it in
// a new tab. Selecting it closes the panel.
export function PanelLink({
  to,
  icon: Icon,
  active = false,
  onClick,
  children,
}: {
  to: string;
  icon: React.ElementType;
  active?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  children: React.ReactNode;
}) {
  return (
    <NavigationMenuLink asChild active={active}>
      <Link to={to} onClick={onClick} className={ROW_CLASS}>
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        {children}
      </Link>
    </NavigationMenuLink>
  );
}

// The shape the Dashboards and Workflows panels share: the entitled items in
// the user's role, then the rest under a second heading, or one note when the
// workspace entitles nothing.
function PersonaSplitPanel<T>({
  inPersona,
  notInPersona,
  renderItems,
  emptyLabel,
  moreLabel,
}: {
  inPersona: T[];
  notInPersona: T[];
  renderItems: (items: T[]) => React.ReactNode;
  emptyLabel: string;
  moreLabel: string;
}) {
  if (inPersona.length === 0 && notInPersona.length === 0) {
    return <MenuNote>{emptyLabel}</MenuNote>;
  }
  return (
    <>
      {inPersona.length > 0 && (
        <>
          <SectionLabel>In your role</SectionLabel>
          {renderItems(inPersona)}
        </>
      )}
      {notInPersona.length > 0 && (
        <>
          {inPersona.length > 0 && <PanelSeparator />}
          <SectionLabel>{moreLabel}</SectionLabel>
          {renderItems(notInPersona)}
        </>
      )}
    </>
  );
}

// The link for the page the user is on carries aria-current, as Learn does.
function DashboardLinks({ cards }: { cards: DashboardCard[] }) {
  const { pathname } = useLocation();
  return (
    <>
      {cards.map((card) => (
        <PanelLink
          key={card.id}
          to={card.route}
          icon={DASHBOARD_ICONS[card.icon] ?? Globe}
          active={pathname.startsWith(card.route)}
        >
          {card.label}
        </PanelLink>
      ))}
    </>
  );
}

// The Dashboards panel, shared by the wide bar and the narrow menu.
export function DashboardsPanel({ visibility }: { visibility: PersonaVisibility }) {
  const { inPersona, notInPersona } = visibility.dashboards;
  const none = inPersona.length === 0 && notInPersona.length === 0;
  if (visibility.status === 'loading' && inPersona.length === 0) {
    return <MenuNote>Loading dashboards…</MenuNote>;
  }
  // The navigation request failed, so an empty list says nothing about the
  // workspace.
  if (visibility.status === 'error' && none) {
    return <MenuNote>Couldn&apos;t load dashboards — reload to retry.</MenuNote>;
  }
  return (
    <PersonaSplitPanel
      inPersona={inPersona}
      notInPersona={notInPersona}
      renderItems={(cards) => <DashboardLinks cards={cards} />}
      emptyLabel="No dashboards in this workspace"
      moreLabel="More dashboards"
    />
  );
}

// An in-page launcher takes a plain left click. A modified click falls
// through to the link, so it still opens the workflow in a new tab.
function isPlainLeftClick(event: React.MouseEvent) {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

// A flat list names a per-franchise audit by its title with a Prefilled chip,
// as the sidebar does. Inside the grouped disclosure the row above already
// says Single Franchise Audit, so each entry shows its franchise and market.
function WorkflowLink({
  entry,
  onLaunchWorkflow,
  grouped = false,
}: {
  entry: BrowseEntry;
  onLaunchWorkflow?: LaunchWorkflow;
  grouped?: boolean;
}) {
  const showFranchise = grouped && entry.kind === 'variant';
  const handleClick = onLaunchWorkflow
    ? (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (!isPlainLeftClick(event)) return;
        event.preventDefault();
        launchInPage(entry, onLaunchWorkflow);
      }
    : undefined;
  return (
    <PanelLink to={browseEntryHref(entry)} icon={entry.agent.outputIcon ?? Workflow} onClick={handleClick}>
      <span className="truncate">{showFranchise ? entry.prefill.franchise : entry.agent.name}</span>
      {entry.kind === 'variant' && <MenuChip>{showFranchise ? entry.prefill.country : 'Prefilled'}</MenuChip>}
    </PanelLink>
  );
}

// Many per-franchise SFA entries fold into one disclosure in their own slot,
// the grouping the sidebar applies.
function WorkflowLinks({ entries, onLaunchWorkflow }: { entries: BrowseEntry[]; onLaunchWorkflow?: LaunchWorkflow }) {
  const renderLinks = (list: BrowseEntry[], grouped = false) =>
    list.map((entry) => (
      <WorkflowLink key={entry.agent.id} entry={entry} onLaunchWorkflow={onLaunchWorkflow} grouped={grouped} />
    ));
  const group = groupSfaVariants(entries);
  if (!group) return <>{renderLinks(entries)}</>;
  const { before, variants, after } = group;
  return (
    <>
      {renderLinks(before)}
      <Collapsible>
        <CollapsibleTrigger className={cn(ROW_CLASS, 'group')}>
          <Store className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          Single Franchise Audit ({variants.length})
          <ChevronRight
            className="ml-auto h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
            aria-hidden="true"
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pl-4">{renderLinks(variants, true)}</CollapsibleContent>
      </Collapsible>
      {renderLinks(after)}
    </>
  );
}

// The Workflows panel. The registry fails closed: on error the panel says so,
// with the sidebar's wording, rather than listing unfiltered. `label` is the
// workspace's name for the section, whose sentence form the notes use.
export function WorkflowsPanel({
  visibility,
  onLaunchWorkflow,
  label,
}: {
  visibility: PersonaVisibility;
  onLaunchWorkflow?: LaunchWorkflow;
  label: WorkflowsLabel;
}) {
  const { inPersona, notInPersona } = visibility.workflows;
  const { noun } = label;
  if (visibility.status === 'error') {
    return <MenuNote>Couldn&apos;t load {noun} — reload to retry.</MenuNote>;
  }
  if (visibility.status === 'loading') {
    return <MenuNote>Loading {noun}…</MenuNote>;
  }
  return (
    <PersonaSplitPanel
      inPersona={inPersona}
      notInPersona={notInPersona}
      renderItems={(entries) => <WorkflowLinks entries={entries} onLaunchWorkflow={onLaunchWorkflow} />}
      emptyLabel={`No ${noun} in this workspace`}
      moreLabel={`More ${noun}`}
    />
  );
}

// Handlers DashAI supplies for its own destinations, so a plain click changes
// the view in place and keeps the open chat. A modified click still follows
// the link, into a new tab.
export interface DashAIActions {
  onOpenChatPage?: () => void;
  onAutonomousAgent?: () => void;
  onScheduledReports?: () => void;
  onSavedCanvas?: () => void;
  onSelectChat?: (chatId: string) => void;
  onHome?: () => void;
}

// The click handler of a link that has an in-page equivalent: a plain click
// runs it instead of navigating, and closes the open panel first.
export function inPageClick(handler: (() => void) | undefined, closePanel: () => void) {
  if (!handler) return undefined;
  return (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    closePanel();
    handler();
  };
}


// The DashAI panel. The two latest chats follow the ways to start one.
// Scheduled reports and Saved canvas are the least used, so they sit under a
// separator at the bottom.
export function DashAIPanel({
  recentChats = [],
  recentChatsLoading = false,
  actions = {},
  closePanel,
}: {
  recentChats?: ChatSession[];
  // True while the first recent-chats list is on its way; the panel holds the
  // space of two chats, so a user with two or more sees nothing move.
  recentChatsLoading?: boolean;
  actions?: DashAIActions;
  closePanel: () => void;
}) {
  const latest = [...recentChats].sort(byLatestMessage).slice(0, RECENT_CHAT_COUNT);
  const holdRows = recentChatsLoading && latest.length === 0;
  return (
    <>
      <PanelLink to={DASHAI_ROUTES.chatPage} icon={Plus} onClick={inPageClick(actions.onOpenChatPage, closePanel)}>
        New chat
      </PanelLink>
      <PanelLink to={DASHAI_ROUTES.autonomousAgent} icon={Zap} onClick={inPageClick(actions.onAutonomousAgent, closePanel)}>
        Autonomous Agent
      </PanelLink>
      {holdRows && (
        <>
          <span className="sr-only" role="status">Loading recent chats</span>
          {Array.from({ length: RECENT_CHAT_COUNT }, (_, i) => (
            <PlaceholderRow key={i} />
          ))}
        </>
      )}
      {latest.map((chat) => (
        <PanelLink
          key={chat.chat_id}
          to={chatRoute(chat.chat_id)}
          icon={MessageSquare}
          onClick={inPageClick(actions.onSelectChat && (() => actions.onSelectChat?.(chat.chat_id)), closePanel)}
        >
          <span className="truncate">{chatDisplayTitle(chat)}</span>
        </PanelLink>
      ))}
      <PanelSeparator />
      <PanelLink to={DASHAI_ROUTES.scheduledReports} icon={Clock} onClick={inPageClick(actions.onScheduledReports, closePanel)}>
        Scheduled reports
      </PanelLink>
      <PanelLink to={DASHAI_ROUTES.savedCanvas} icon={Bookmark} onClick={inPageClick(actions.onSavedCanvas, closePanel)}>
        Saved canvas
      </PanelLink>
    </>
  );
}

// An entry whose label is a link to its page and whose chevron discloses the
// panel, so the page is one click away without hiding the panel's other
// destinations.
export function SplitTopMenu({
  value,
  label,
  icon: Icon,
  to,
  active,
  onClick,
  contentClassName = 'w-64',
  children,
}: {
  value: string;
  label: string;
  icon: React.ElementType;
  to: string;
  active?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  contentClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <NavigationMenuItem value={value} className="flex items-center">
      <NavigationMenuLink asChild active={active}>
        <Link to={to} onClick={onClick} className={cn(navPillClass(active), 'rounded-r-none pr-2')}>
          <Icon className="h-4 w-4" aria-hidden="true" />
          {label}
        </Link>
      </NavigationMenuLink>
      <NavigationMenuTrigger aria-label={`More ${label}`} className={cn(navPillClass(active), 'rounded-l-none px-1.5')} />
      <NavigationMenuContent className={contentClassName}>{children}</NavigationMenuContent>
    </NavigationMenuItem>
  );
}

// One entry in the bar that discloses a panel of links. `value` names the
// panel, so the bar can tell which one is open. It is fixed rather than the
// label, which a workspace can rename.
export function TopMenu({ value, label, icon: Icon, active, contentClassName = 'w-64', children }: {
  value: string;
  label: string;
  icon: React.ElementType;
  active?: boolean;
  contentClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <NavigationMenuItem value={value}>
      <NavigationMenuTrigger className={navPillClass(active)}>
        <Icon className="h-4 w-4" aria-hidden="true" />
        {label}
      </NavigationMenuTrigger>
      <NavigationMenuContent className={contentClassName}>{children}</NavigationMenuContent>
    </NavigationMenuItem>
  );
}
